import { Card, IconButton, SyncStatus } from '@/components/ui'
import { EditIcon, ImageIcon, MapPinIcon, TrashIcon } from '@/components/icons'
import type { Place, SyncSaveState } from '@/types'
import { usePlaceThumbnail } from './usePlaceThumbnail'

type PlaceCardProps = {
  place: Place
  syncState: SyncSaveState | null
  onEdit: (place: Place) => void
  onDelete: (place: Place) => void
}

export function PlaceCard({ place, syncState, onEdit, onDelete }: PlaceCardProps) {
  const { url, loading } = usePlaceThumbnail(place)
  const locationParts = [place.category, place.subzone, place.exactLocation].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  )

  return (
    <Card padding="sm" className="places-card">
      <div className="places-card-media">
        {url ? (
          <img className="places-card-img" src={url} alt={`Foto van ${place.objectLabel}`} loading="lazy" />
        ) : (
          <div className="places-card-placeholder" role="img" aria-label={loading ? 'Foto wordt geladen' : 'Geen foto'}>
            <ImageIcon size={28} />
            <span>{loading ? 'Foto laden…' : 'Geen foto'}</span>
          </div>
        )}
      </div>
      <div className="places-card-body">
        <h3 className="places-card-title">{place.objectLabel}</h3>
        <p className="places-card-location">
          <MapPinIcon size={16} />
          <span>{locationParts.join(' · ')}</span>
        </p>
        {place.notes ? <p className="places-card-notes">{place.notes}</p> : null}
        <SyncStatus state={syncState} />
      </div>
      <div className="places-card-actions">
        <IconButton aria-label={`Bewerk ${place.objectLabel}`} onClick={() => onEdit(place)}>
          <EditIcon />
        </IconButton>
        <IconButton
          aria-label={`Verwijder ${place.objectLabel}`}
          variant="danger"
          onClick={() => onDelete(place)}
        >
          <TrashIcon />
        </IconButton>
      </div>
    </Card>
  )
}
